const KEY_STORAGE = "chinapie_ops_admin_key";
const PROFILE_STORAGE = "chinapie_ops_profile";

export type OpsProfile = {
  name: string;
  role: string;
  username?: string | null;
  source?: string;
};

export function getApiBase(): string {
  const raw = import.meta.env.VITE_API_BASE_URL || "https://api.chinapie.ai";
  return String(raw).replace(/\/$/, "");
}

export function getAdminKey(): string {
  return sessionStorage.getItem(KEY_STORAGE) || "";
}

export function setAdminKey(key: string): void {
  sessionStorage.setItem(KEY_STORAGE, key.trim());
}

export function getProfile(): OpsProfile | null {
  const raw = sessionStorage.getItem(PROFILE_STORAGE);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as OpsProfile;
  } catch {
    return null;
  }
}

export function setProfile(profile: OpsProfile): void {
  sessionStorage.setItem(PROFILE_STORAGE, JSON.stringify(profile));
}

export function clearAdminKey(): void {
  sessionStorage.removeItem(KEY_STORAGE);
  sessionStorage.removeItem(PROFILE_STORAGE);
}

export class OpsApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export async function opsFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const key = getAdminKey();
  if (!key) throw new OpsApiError("Not signed in", 401, "OPS_UNAUTHORIZED");
  const headers = new Headers(init.headers);
  headers.set("X-ChinaPie-Content-Admin-Key", key);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(`${getApiBase()}${path}`, { ...init, headers });
  if (!res.ok) {
    let code: string | undefined;
    let message = res.statusText;
    try {
      const body = (await res.json()) as {
        detail?: { error?: { code?: string; message?: string } };
      };
      code = body.detail?.error?.code;
      message = body.detail?.error?.message || code || message;
    } catch {
      /* ignore */
    }
    throw new OpsApiError(message || "Request failed", res.status, code);
  }
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("text/csv")) {
    return (await res.text()) as T;
  }
  return (await res.json()) as T;
}

export async function loginWithPassword(
  username: string,
  password: string,
): Promise<{ token: string; profile: OpsProfile }> {
  const res = await fetch(`${getApiBase()}/api/ops/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    throw new OpsApiError("Wrong username or password", res.status, "OPS_BAD_LOGIN");
  }
  const body = (await res.json()) as {
    data: { token: string; name: string; role: string; username?: string | null; source?: string };
  };
  return {
    token: body.data.token,
    profile: {
      name: body.data.name,
      role: body.data.role,
      username: body.data.username,
      source: body.data.source,
    },
  };
}
